
import { mapMutations } from "vuex";
import ButtonCustom from "@/components/shared/buttons/ButtonCustom.vue";
import ConfirmHoanThanh from "@/components/shared/confirm-hoan-thanh-modal/ConfirmHoanThanh.vue";
import Setting from "@/components/shared/setting/Setting.vue";
import type { SettingAction } from "@/components/shared/setting/Setting.vue";
import { formatAmount } from "@/core/helpers/number-format";
import {
  getErrorMsg,
  mapPhieuNhapXuatStatusToLabel,
  mapPhieuNhapXuatStatusToLevelColor,
  swalConfirm,
  swalNotification,
  getDuyetPhieuNhapXuatStatus,
  swalErrNotification,
} from "@/core/helpers/utils";
import { useButtonCustom } from "@/core/hooks/use-button-custom";
import { useDateTime } from "@/core/hooks/use-date-time-hook";
import { useLoading } from "@/core/hooks/use-loading";
import { usePagination } from "@/core/hooks/use-pagination";
import { useRouterCustom } from "@/core/hooks/use-router-custom";
import { useEnv } from "@/core/hooks/useEnv";
import usePermission from "@/core/hooks/usePermission";
import usePhieuNhap from "@/core/hooks/usePhieuNhap";
import { IPhieuNhapKhoResponseBase } from "@/core/interfaces/ApiResponses";
import { PhieuNhapKhoService } from "@/core/services/PhieuNhapKho.service";
import { getChiTietPhieuNhapKhoParams } from "@/components/phieu-nhap-kho";
import { openBienBanKiemNghiemPdf } from "@/components/shared/pdf-viewer/open-bien-ban-kiem-nghiem-pdf";
import { openPhieuNhapKhoPdf } from "@/components/shared/pdf-viewer/open-phieu-nhap-kho-pdf";
import { defineComponent, markRaw, PropType } from "vue";
import { useRouter } from "vue-router";
import { PhieuNhapXuatStatusEnum } from "@/core/enums/phieuNhapXuatStatus.enum";
import IconCircleCheckClassicRegular from "@/icons/IconCircleCheckClassicRegular.vue";
import IconEye from "@/icons/IconEye.vue";
import IconMemoCircleCheckClassicSolid from "@/icons/IconMemoCircleCheckClassicSolid.vue";
import IconPencil from "@/icons/IconPencil.vue";
import IconPrint from "@/icons/IconPrint.vue";
import IconTrash from "@/icons/IconTrash.vue";

interface PhieuNhapKhoTableAction {
  buttonType: string;
  colorType?: string | ((row: IPhieuNhapKhoResponseBase) => string | undefined);
  tooltipMessage: string | ((row: IPhieuNhapKhoResponseBase) => string);
  condition?: (row: IPhieuNhapKhoResponseBase) => boolean;
  isDisabled?: boolean | ((row: IPhieuNhapKhoResponseBase) => boolean);
  onClick: (row: IPhieuNhapKhoResponseBase) => void | Promise<unknown>;
  icon?: SettingAction["icon"];
  label?: string | ((row: IPhieuNhapKhoResponseBase) => string);
  tooltip?: string | ((row: IPhieuNhapKhoResponseBase) => string);
  any?: string[];
}

export default defineComponent({
  name: "phieu-nhap-kho-table-v2",

  emits: ["on-detail-button-click", "refresh"],

  components: {
    ButtonCustom,
    ConfirmHoanThanh,
    Setting,
  },

  setup() {
    const { ButtonsType, ButtonTypeColors } = useButtonCustom();

    const { indexCal } = usePagination();

    const { push } = useRouterCustom();

    const { push: pushRouter } = useRouter();

    const { formatDisplayingDate } = useDateTime();

    const { editableNgayHoanThanhPhieuNhap } = useEnv();

    const { startLoading, endLoading } = useLoading();

    const { editable, goToEditPhieuNhap } = usePhieuNhap();

    const { canDuyetPnk } = usePermission();

    return {
      indexCal,
      ButtonsType,
      ButtonTypeColors,
      canDuyetPnk,
      formatDisplayingDate,
      push,
      pushRouter,
      editableNgayHoanThanhPhieuNhap,
      startLoading,
      endLoading,
      editable,
      formatAmount,
      goToEditPhieuNhap,
    };
  },

  props: {
    curPage: {
      type: Number as PropType<number>,
      default: 0,
    },
    items: {
      type: Array as PropType<Array<IPhieuNhapKhoResponseBase>>,
      default: () => [],
    },
    idKho: {
      type: Number as PropType<number>,
      default: 0,
    },
    perPage: {
      type: Number as PropType<number>,
      default: 0,
    },
    showXacNhanButton: {
      type: Boolean as PropType<boolean>,
      default: true,
    },
    showPheDuyetButton: {
      type: Boolean as PropType<boolean>,
      default: true,
    },
    showDeleteButton: {
      type: Boolean as PropType<boolean>,
      default: true,
    },
    showEditButton: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
    showDuyetColumn: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
    showKhoColumn: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
    /**
     * Custom action buttons array. If provided, these buttons will be used instead of default buttons.
     * Each button object should contain:
     * - buttonType: string - The button type from ButtonsType enum
     * - colorType: string - The color type from ButtonTypeColors enum
     * - tooltipMessage: string - Tooltip text for the button
     * - condition?: (row: any) => boolean - Optional condition function to show/hide button
     * - onClick: (row: any) => void - Click handler function
     * - icon?: string - Optional icon identifier
     */
    actionButtons: {
      type: Array as PropType<Array<PhieuNhapKhoTableAction>>,
      default: () => [],
    },
  },

  computed: {
    /**
     * Default action buttons configuration
     */
    defaultActionButtons() {
      const buttons: PhieuNhapKhoTableAction[] = [];

      // Info button (always shown)
      buttons.push({
        buttonType: this.ButtonsType.INFO,
        colorType: this.ButtonTypeColors.INFO,
        tooltipMessage: "Chi tiết phiếu nhập kho",
        onClick: (row: IPhieuNhapKhoResponseBase) => {
          this.pushRouter({
            name: "chi-tiet-phieu-nhap-kho",
            params: getChiTietPhieuNhapKhoParams(row.id),
          });
        },
      });

      // Edit button
      if (this.showEditButton) {
        buttons.push({
          buttonType: this.ButtonsType.EDIT,
          colorType: this.ButtonTypeColors.SUCCESS,
          tooltipMessage: "Chỉnh sửa",
          condition: (row: IPhieuNhapKhoResponseBase) =>
            this.editable(row) &&
            row.processing_status === PhieuNhapXuatStatusEnum.PENDING,
          onClick: (row: IPhieuNhapKhoResponseBase) =>
            this.$router.push(this.goToEditPhieuNhap(row)),
        });
      }

      // Print button (always shown)
      buttons.push({
        buttonType: this.ButtonsType.PRINT,
        colorType: this.ButtonTypeColors.SECONDARY,
        tooltipMessage: "In phiếu",
        onClick: (row: IPhieuNhapKhoResponseBase) =>
          openPhieuNhapKhoPdf(this.$router, row.id),
      });

      // Delete button
      if (this.showDeleteButton) {
        buttons.push({
          buttonType: this.ButtonsType.TRASH,
          colorType: this.ButtonTypeColors.DANGER,
          tooltipMessage: "Xoá phiếu",
          any: ["data:pnk:xoa"],
          condition: (row: IPhieuNhapKhoResponseBase) => !row.is_approved,
          onClick: (row: IPhieuNhapKhoResponseBase) => {
            this.deletePhieuNhap(row);
          },
        });
      }

      // Approve/Done button
      if (this.showXacNhanButton && this.canDuyetPnk) {
        // Show APPROVE button when is_approved = false
        buttons.push({
          buttonType: this.ButtonsType.CONFIRM,
          colorType: this.ButtonTypeColors.WARNING,
          tooltipMessage: "Xác nhận",
          any: ["data:pnk:duyet"],
          isDisabled: (row: IPhieuNhapKhoResponseBase) =>
            this.getDuyetPhieuNhapXuatStatus(row.processing_status).isDisabled,
          condition: (row: IPhieuNhapKhoResponseBase) =>
            this.getDuyetPhieuNhapXuatStatus(row.processing_status).isDisplay,
          onClick: (row: IPhieuNhapKhoResponseBase) => {
            this.selectingPhieuNhap = row;
            this.isApprovalFlow = true;
          },
        });
      }

      // CEO signature — one-way sign only (no unsign)
      if (this.showPheDuyetButton) {
        buttons.push({
          buttonType: this.ButtonsType.APPROVE,
          colorType: this.ButtonTypeColors.SUCCESS,
          tooltipMessage: "Giám đốc ký duyệt",
          label: "Giám đốc ký duyệt",
          any: ["data:pnk:duyet"],
          condition: (row: IPhieuNhapKhoResponseBase) => !row.is_admin_signed,
          onClick: (row: IPhieuNhapKhoResponseBase) =>
            this.handleAdminSign(row),
        });
      }

      return buttons;
    },

    /**
     * Determines whether to show default buttons or custom action buttons
     */
    shouldShowDefaultButtons() {
      return this.actionButtons.length === 0;
    },
  },

  data() {
    return {
      selectingPhieuNhap: null as IPhieuNhapKhoResponseBase | null,
      isApprovalFlow: false, // Flag to track if we're in approval or completion flow
    };
  },

  methods: {
    /**
     * Get visible action buttons based on their conditions
     */
    ...mapMutations("ConfirmationDialogModule", {
      setDialog: "setDialog",
      closeDialog: "closeDialog",
    }),
    mapPhieuNhapXuatStatusToLevelColor,
    mapPhieuNhapXuatStatusToLabel,
    getDuyetPhieuNhapXuatStatus,
    getVisibleActionConfigs(row: IPhieuNhapKhoResponseBase) {
      const buttonsToUse = this.shouldShowDefaultButtons
        ? this.defaultActionButtons
        : this.actionButtons;

      return buttonsToUse.filter((button) => {
        if (button.condition) {
          return button.condition(row);
        }

        return true;
      });
    },

    getVisibleSettingActions(row: IPhieuNhapKhoResponseBase): SettingAction[] {
      return this.getVisibleActionConfigs(row).map((actionConfig) => {
        const resolveText = (
          value:
            | string
            | ((row: IPhieuNhapKhoResponseBase) => string)
            | undefined
        ) => (typeof value === "function" ? value(row) : value);

        return {
          icon:
            actionConfig.icon ||
            this.getIconForButtonType(actionConfig.buttonType),
          label:
            resolveText(actionConfig.label) ||
            resolveText(actionConfig.tooltipMessage) ||
            "",
          tooltip:
            resolveText(actionConfig.tooltip) ||
            resolveText(actionConfig.tooltipMessage),
          any: actionConfig.any,
          isDisabled: this.isActionDisabled(actionConfig, row),
          onClick: () => {
            if (this.isActionDisabled(actionConfig, row)) {
              return;
            }

            return actionConfig.onClick(row);
          },
        };
      });
    },

    isActionDisabled(
      button: PhieuNhapKhoTableAction,
      row: IPhieuNhapKhoResponseBase
    ): boolean {
      if (typeof button.isDisabled === "function") {
        return button.isDisabled(row);
      }

      return button.isDisabled || false;
    },

    getIconForButtonType(buttonType: string) {
      switch (buttonType) {
        case this.ButtonsType.EDIT:
          return markRaw(IconPencil);
        case this.ButtonsType.TRASH:
          return markRaw(IconTrash);
        case this.ButtonsType.PRINT:
          return markRaw(IconPrint);
        case this.ButtonsType.APPROVE:
        case this.ButtonsType.DONE:
        case this.ButtonsType.CIRCLE_CHECK_CLASSIC_REGULAR:
          return markRaw(IconCircleCheckClassicRegular);
        case this.ButtonsType.CONFIRM:
        case this.ButtonsType.MEMO_CIRCLE_CHECK_CLASSIC_LIGHT:
        case this.ButtonsType.MEMO_CIRCLE_CHECK_CLASSIC_SOLID:
          return markRaw(IconMemoCircleCheckClassicSolid);
        case this.ButtonsType.INFO:
        case this.ButtonsType.EYE:
        case this.ButtonsType.DETAIL:
        default:
          return markRaw(IconEye);
      }
    },

    openDetail(row: IPhieuNhapKhoResponseBase) {
      this.$emit("on-detail-button-click", {
        phieuNhapKhoData: row,
      });
    },
    openBienBanPdfViewer(phieuNhapKhoDetail: IPhieuNhapKhoResponseBase) {
      const idBienBan =
        phieuNhapKhoDetail.id_bien_ban_kiem_nghiem ??
        phieuNhapKhoDetail.bien_ban_kiem_nghiem?.id;

      openBienBanKiemNghiemPdf(this.$router, idBienBan);
    },
    confirmHoanThanh() {
      this.selectingPhieuNhap = null;
      this.isApprovalFlow = false;
      this.$emit("refresh");
    },
    async deletePhieuNhap(phieuNhap: IPhieuNhapKhoResponseBase) {
      const confirmed = await swalConfirm({
        title: "Xác nhận xoá",
        text: `Bạn có chắc muốn xoá phiếu số ${phieuNhap.so_phieu}?`,
        icon: "warning",
        confirmButtonText: "Xoá",
        confirmButtonClass: "btn btn-danger",
      });

      if (!confirmed) return;

      try {
        await PhieuNhapKhoService.delete(phieuNhap.id);
        this.$emit("refresh");
      } catch (error) {
        await swalNotification(
          getErrorMsg(error, "Có lỗi xảy ra khi xoá phiếu nhập này"),
          "error"
        );
      }
    },
    handleAdminSign(phieuNhapData: IPhieuNhapKhoResponseBase) {
      if (phieuNhapData.is_admin_signed) return;

      this.setDialog({
        title: `Phiếu nhập số ${phieuNhapData.so_phieu}`,
        message: "Xác nhận giám đốc ký duyệt phiếu nhập này?",
        onConfirm: async () => {
          this.closeDialog();
          await this.startLoading();

          try {
            const {
              data: { message },
            } = await PhieuNhapKhoService.adminSign(phieuNhapData.id, {
              is_admin_signed: true,
            });
            await this.endLoading();
            await swalNotification(
              message || "Giám đốc ký duyệt phiếu nhập thành công"
            );
            this.$emit("refresh");
          } catch (e) {
            await this.endLoading();

            await swalErrNotification(e, "Có lỗi xảy ra");
          }
        },
      });
    },
  },
});
